import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { BaseERC20, BaseERC20Factory, JointCampaign } from '@arcxgame/contracts/dist/src/typings'
import { calculateAPY } from '@arcxmoney/arcx-core'
import dayjs from 'dayjs'
import { BigNumber, constants, ContractTransaction, Signer } from 'ethers'
import { Network } from 'src/config'
import { BASE } from 'src/constants/crypto'
import { getReadableNumber } from 'src/helpers/getReadableNumber'
import { CoingeckoApi } from 'src/services/CoingeckoApi'
import ArcFarm from './ArcFarm'
import { IPeriodEndDate } from './IPeriodEndDate'
import { IRewardPair } from './IRewardPair'

export class JointCampaignFarm extends ArcFarm {
  constructor(
    name: string,
    platform: string,
    pair: string,
    pairLink: string,
    note: string,
    signer: Signer,
    network: Network,
    public contract: JointCampaign,
    client: ApolloClient<NormalizedCacheObject>,
  ) {
    super(name, platform, pair, pairLink, note, signer, network, contract, client)
  }

  /* ========== Getters ========== */

  hasDebtRequirement(): boolean {
    return true
  }

  async getIsRewardClaimable(): Promise<boolean> {
    const arcTokensClaimable = this.contract.arcTokensClaimable()
    const collabTokensClaimable = this.contract.collabTokensClaimable()

    return arcTokensClaimable || collabTokensClaimable
  }

  async isRewardPeriodFinished(): Promise<boolean> {
    const arcPeriodFinish: BigNumber = await this.contract.arcPeriodFinish()
    const collabPeriodFinish: BigNumber = await this.contract.collabPeriodFinish()

    const now = Math.round(Date.now() / 1000)
    const isEnded = arcPeriodFinish.lte(now) || collabPeriodFinish.lte(now)

    return isEnded
  }

  async getApys(): Promise<BigNumber[]> {
    const isEnded = await this.isRewardPeriodFinished()

    if (isEnded) return []

    const [arcRewardtoken, collabRewardtoken] = await this.getRewardTokens()
    const totalArcRewards = await arcRewardtoken.balanceOf(this.contract.address)
    const totalCollabRewards = await collabRewardtoken.balanceOf(this.contract.address)

    const stakedUSDValue = await this.contract.totalSupply()
    const rewardDurationSecs = await this.contract.rewardsDuration()
    const priceOfArcToken = await CoingeckoApi.getUsdPrice(arcRewardtoken.address)
    const collabToken = await this.contract.collabRewardToken()
    const priceOfCollabToken = await CoingeckoApi.getUsdPrice(collabToken)

    if (stakedUSDValue.isZero()) {
      return [constants.Zero]
    }

    const daoAllocation = await this.contract.daoAllocation()
    const totalArcRewardsMinusDaoAllocation = totalArcRewards
      .mul(constants.WeiPerEther.sub(daoAllocation))
      .div(BASE)

    const apys = []
    apys.push(
      calculateAPY(
        totalArcRewardsMinusDaoAllocation,
        rewardDurationSecs,
        priceOfArcToken,
        stakedUSDValue,
      ),
    )
    apys.push(
      calculateAPY(totalCollabRewards, rewardDurationSecs, priceOfCollabToken, stakedUSDValue),
    )

    return apys
  }

  async getTotalRewardsString(): Promise<string> {
    const arcToken = BaseERC20Factory.connect(await this.contract.arcRewardToken(), this.signer)
    const collabToken = BaseERC20Factory.connect(
      await this.contract.collabRewardToken(),
      this.signer,
    )

    const arcSymbol = await arcToken.symbol()
    const collabSymbol = await collabToken.symbol()

    const arcReward = await arcToken.balanceOf(this.contract.address)
    const collabReward = await collabToken.balanceOf(this.contract.address)

    return `${getReadableNumber(arcReward)} ${arcSymbol} + ${getReadableNumber(
      collabReward,
    )} ${collabSymbol}`
  }

  async getTotalRewards(): Promise<IRewardPair[]> {
    const arcToken = BaseERC20Factory.connect(await this.contract.arcRewardToken(), this.signer)
    const collabToken = BaseERC20Factory.connect(
      await this.contract.collabRewardToken(),
      this.signer,
    )

    const arcSymbol = await arcToken.symbol()
    const collabSymbol = await collabToken.symbol()

    const arcReward = await arcToken.balanceOf(this.contract.address)
    const collabReward = await collabToken.balanceOf(this.contract.address)

    return [
      {
        symbol: arcSymbol,
        amount: arcReward,
      },
      {
        symbol: collabSymbol,
        amount: collabReward,
      },
    ]
  }

  async getEarningsString(): Promise<string> {
    const signerAddress = await this.signer.getAddress()

    const arcEarned = await (
      await this.contract.arcEarned(signerAddress)
    ).mul(BigNumber.from(10).pow(6))
    const collabEarned = await this.contract.collabEarned(signerAddress)

    const collabToken = BaseERC20Factory.connect(
      await this.contract.collabRewardToken(),
      this.signer,
    )

    const collabSymbol = await collabToken.symbol()

    return `${getReadableNumber(arcEarned)} GRAEBERS + ${getReadableNumber(
      collabEarned,
    )} ${collabSymbol}`
  }

  async getEarnings(): Promise<IRewardPair[]> {
    const signerAddress = await this.signer.getAddress()

    const arcEarned = await (
      await this.contract.arcEarned(signerAddress)
    ).mul(BigNumber.from(10).pow(6))
    const collabEarned = await this.contract.collabEarned(signerAddress)

    const collabToken = BaseERC20Factory.connect(
      await this.contract.collabRewardToken(),
      this.signer,
    )

    const collabSymbol = await collabToken.symbol()

    return [
      {
        symbol: 'GRAEBERS',
        amount: arcEarned,
      },
      {
        symbol: collabSymbol,
        amount: collabEarned,
      },
    ]
  }

  async getRewardTokens(): Promise<BaseERC20[]> {
    const arcToken = BaseERC20Factory.connect(await this.contract.arcRewardToken(), this.signer)
    const collabtoken = BaseERC20Factory.connect(
      await this.contract.collabRewardToken(),
      this.signer,
    )

    return [arcToken, collabtoken]
  }

  async getPeriodFinish(): Promise<IPeriodEndDate[]> {
    const arcToken = BaseERC20Factory.connect(await this.contract.arcRewardToken(), this.signer)
    const collabToken = BaseERC20Factory.connect(
      await this.contract.collabRewardToken(),
      this.signer,
    )

    const arcPeriodFinish = await this.contract.arcPeriodFinish()
    const collabPeriodFinish = await this.contract.collabPeriodFinish()

    const arcSymbol = await arcToken.symbol()
    const collabSymbol = await collabToken.symbol()

    return [
      {
        symbol: arcSymbol,
        endDateString: dayjs(arcPeriodFinish.toNumber() * 1000).format('DD / MM / YYYY'),
      },
      {
        symbol: collabSymbol,
        endDateString: dayjs(collabPeriodFinish.toNumber() * 1000).format('DD / MM / YYYY'),
      },
    ]
  }

  async getAvailableToStake(): Promise<BigNumber> {
    const stakingToken = BaseERC20Factory.connect(await this.contract.stakingToken(), this.signer)

    const currentDebt = await this.getCurrentDebt()
    const debtToStakeRatio = BigNumber.from(await this.contract.stakeToDebtRatio())
    const stakedBalance = await this.getStakedBalance()
    const stakableBalance = await stakingToken.balanceOf(await this.signer.getAddress())

    return this._getAvailableToStake(
      currentDebt,
      debtToStakeRatio,
      stakedBalance,
      stakableBalance,
      true,
    )
  }

  /* ========== Modifiers ========== */

  async stake(amount: BigNumber): Promise<ContractTransaction> {
    const stakableMax = await this.getAvailableToStake()

    if (amount.gt(stakableMax)) {
      throw Error('You do not have minted enough debt!')
    }

    const stakingToken = await this.getStakingToken()
    const allowance = await stakingToken.allowance(
      await this.signer.getAddress(),
      this.contract.address,
    )

    if (allowance.lt(amount)) {
      try {
        const tx = await stakingToken.approve(this.contract.address, amount.toString())
        await tx.wait()
      } catch {
        // If an approval is failing it's probably because it's a curve token LP token wanting
        // the approval to be set to 0 smh...
        const resetApproval = await stakingToken.approve(this.contract.address, 0)
        await resetApproval.wait()

        const approveTx = await stakingToken.approve(this.contract.address, amount.toString())
        await approveTx.wait()
      }
    }

    try {
      const position = await this.getLargestDebtPosition()

      const stakeTx = await this.contract.stake(amount, position.id.split('-')[1])
      return stakeTx
    } catch (err) {
      console.error(err)
      throw Error('An error occured during staking')
    }
  }
}
